import { Command } from 'cmdk'
import {
  Clock,
  FileText,
  FolderPlus,
  HardDrive,
  History,
  Plus,
  SlidersHorizontal,
  Sparkles,
} from 'lucide-react'
import type { Document, SearchResult, SuggestItem } from '@agentwiki/shared'
import { cn } from '../../lib/utils'
import { CommandPaletteItem } from './command-palette-item'

interface CommandPaletteListProps {
  query: string
  isDark: boolean
  isSearching: boolean
  suggestions: SuggestItem[]
  searchResults: SearchResult[]
  recentDocuments: Document[]
  showSuggestions: boolean
  showSearch: boolean
  showRecent: boolean
  canCreateDocument: boolean
  canCreateFolder: boolean
  onNewDocument: () => void
  onNewFolder: () => void
  onOpenFullSearch: () => void
  onSuggestion: (item: SuggestItem) => void
  onOpenUpload: (result: SearchResult) => void
  onOpenSearchResult: (result: SearchResult) => void
  onOpenRecentDocument: (document: Document) => void
}

function groupHeading(label: string, isDark: boolean) {
  return (
    <span
      className={cn(
        'px-2 py-1 text-[11px] font-semibold uppercase tracking-wider',
        isDark ? 'text-neutral-400' : 'text-neutral-500',
      )}
    >
      {label}
    </span>
  )
}

function suggestionIcon(source: SuggestItem['source']) {
  if (source === 'history') return <History className="h-3.5 w-3.5" />
  if (source === 'fuzzy') return <Sparkles className="h-3.5 w-3.5" />
  return <FileText className="h-3.5 w-3.5" />
}

export function CommandPaletteList(props: CommandPaletteListProps) {
  const { query, isDark } = props

  return (
    <Command.List className="max-h-80 overflow-y-auto p-2">
      <Command.Empty
        className={cn(
          'px-3 py-6 text-center text-sm',
          isDark ? 'text-neutral-400' : 'text-neutral-500',
        )}
      >
        {props.isSearching ? 'Searching...' : 'No results found.'}
      </Command.Empty>

      <Command.Group heading={groupHeading('Actions', isDark)}>
        {props.canCreateDocument && (
          <CommandPaletteItem
            icon={<Plus className="h-3.5 w-3.5" />}
            label={query ? `Create "${query}"` : 'New document'}
            onSelect={props.onNewDocument}
            isDark={isDark}
          />
        )}
        {props.canCreateFolder && (
          <CommandPaletteItem
            icon={<FolderPlus className="h-3.5 w-3.5" />}
            label={query ? `New folder "${query}"` : 'New folder'}
            onSelect={props.onNewFolder}
            isDark={isDark}
          />
        )}
        <CommandPaletteItem
          icon={<SlidersHorizontal className="h-3.5 w-3.5" />}
          label={query ? `Open full search for "${query}"` : 'Open full search'}
          onSelect={props.onOpenFullSearch}
          isDark={isDark}
        />
      </Command.Group>

      {props.showSuggestions && (
        <Command.Group heading={groupHeading('Suggestions', isDark)}>
          {props.suggestions.map((item, index) => (
            <CommandPaletteItem
              key={`suggest-${index}`}
              icon={suggestionIcon(item.source)}
              label={item.text}
              sublabel={item.source === 'history' ? 'recent search' : undefined}
              onSelect={() => props.onSuggestion(item)}
              isDark={isDark}
            />
          ))}
        </Command.Group>
      )}

      {props.showSearch && (
        <Command.Group heading={groupHeading('Search results', isDark)}>
          {props.searchResults.map((result) => {
            const isUpload = result.resultType === 'upload'
            return (
              <CommandPaletteItem
                key={result.id}
                icon={
                  isUpload ? (
                    <HardDrive className="h-3.5 w-3.5" />
                  ) : (
                    <FileText className="h-3.5 w-3.5" />
                  )
                }
                label={result.title}
                sublabel={result.snippet ?? result.category ?? (isUpload ? 'storage' : undefined)}
                onSelect={
                  isUpload
                    ? () => props.onOpenUpload(result)
                    : () => props.onOpenSearchResult(result)
                }
                isDark={isDark}
              />
            )
          })}
        </Command.Group>
      )}

      {props.showRecent && (
        <Command.Group heading={groupHeading('Recent', isDark)}>
          {props.recentDocuments.map((document) => (
            <CommandPaletteItem
              key={document.id}
              icon={<Clock className="h-3.5 w-3.5" />}
              label={document.title}
              sublabel={document.category ?? undefined}
              onSelect={() => props.onOpenRecentDocument(document)}
              isDark={isDark}
            />
          ))}
        </Command.Group>
      )}
    </Command.List>
  )
}
